import React, { useCallback, useEffect, useRef, useState } from "react";
import { usePermissions } from "../../hooks/usePermissions";
import { listClientConversation, replyToClient } from "../../helpers/clientView/clientViewShare";
import { formatMessageTime, friendlyError } from "../../helpers/clientView/clientViewModel";
import { isOffline } from "../../helpers/connectivity";
import { RecBar, useKeepFocusedFieldVisible, useKeyboardOpen } from "../materials/MaterialRecordParts";
import "./clientViewManage.css";

/**
 * The contractor's side of the Client Conversation. Separate from internal
 * picture comments, which are never part of this.
 */
export function ClientConversationScreen({ projectId, projectName, writable = true, onBack }) {
  const { actor } = usePermissions();
  useKeyboardOpen(true);
  useKeepFocusedFieldVisible(true);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const offline = isOffline();

  const reload = useCallback(async () => {
    try {
      setMessages(await listClientConversation(actor, projectId));
      setError("");
    } catch (err) {
      setError(friendlyError(err, "Unable to load the conversation."));
    } finally {
      setLoading(false);
    }
  }, [actor, projectId]);

  useEffect(() => {
    reload();
    const timer = setInterval(() => {
      if (document.visibilityState !== "hidden") reload();
    }, 30000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: "end" });
  }, [messages.length]);

  async function send() {
    if (sending || !body.trim()) return;
    setSending(true);
    setError("");
    try {
      const message = await replyToClient(actor, projectId, body);
      setMessages((current) => [...current, message]);
      setBody("");
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSending(false);
    }
  }

  const canReply = writable && !offline;

  return (
    <div className="screen nx-screen rec-page" data-testid="client-conversation">
      <RecBar title="Client Conversation" onBack={onBack} />
      {projectName ? <div className="tk-subtitle">{projectName}</div> : null}
      <div className="rec-body">
        <p className="cvm-help">
          This is the conversation your client sees in Client View. Internal picture comments are never shown here.
        </p>
        {error ? (
          <div className="rec-banner" role="alert">
            {error}
          </div>
        ) : null}
        {offline ? <div className="tk-notice">Reconnect to read and reply.</div> : null}
        {!writable ? <div className="tk-notice">This project is archived. Replies are turned off.</div> : null}

        {loading ? (
          <p className="pj-empty">Loading…</p>
        ) : messages.length === 0 ? (
          <p className="pj-empty" data-testid="conversation-empty">
            No messages yet. Your client can write to you from their link.
          </p>
        ) : (
          <div className="cvm-thread" data-testid="conversation-thread">
            {messages.map((m) => (
              <div key={m.message_id} className={`cvm-msg cvm-msg--${m.sender_type}`}>
                <div className="cvm-msg__meta">
                  {m.sender_type === "client" ? <b className="cvm-msg__tag">Client</b> : null}
                  {m.sender_label} · {formatMessageTime(m.created_at)}
                </div>
                <div className="cvm-msg__bubble">{m.body}</div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
        )}
        <div className="pj-pagepad" aria-hidden="true" />
      </div>

      <div className="rec-actions cvm-compose">
        <textarea
          className="rec-input rec-input--area"
          rows={1}
          maxLength={2000}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={canReply ? "Reply to your client…" : "Replies are unavailable"}
          aria-label="Reply"
          disabled={!canReply}
        />
        <button type="button" className="rec-btn rec-btn--primary cvm-send" disabled={!canReply || sending || !body.trim()} onClick={send}>
          {sending ? "…" : "Send"}
        </button>
      </div>
    </div>
  );
}
